/**
 * Analytics: the engine's own reference tables. The score trend chart went with the
 * dashboard's trend query on 23 September, when the overall score was removed.
 *
 * Showing the band table in-app matters for trust: a clinician can see exactly
 * which threshold produced a score, straight from the seeded engine version.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Card,
  PageHeader,
  Pill,
  Table,
  Td,
  Th,
} from '../components/ui';

interface EngineData {
  version: string;
  overallFormula: string;
  sourceSha256: string;
  seededAt: string;
  biomarkers: Array<{
    markerId: string;
    name: string;
    domain: string;
    type: string;
    unit: string;
    defaultWeightPct: number;
    requiredForDomain: boolean;
    sexRule: string;
  }>;
  domainGates: Array<{
    domain: string;
    minMarkers: number;
    ofMarkers: number | null;
    minWeightCoveragePct: number;
    hardRequired: string[];
  }>;
  scoreBands?: Array<{
    marker: string;
    sex: string;
    unit: string;
    lower: number;
    lowerIncl: boolean;
    upper: number;
    upperIncl: boolean;
    score: number;
    label: string;
    priority: string;
    escalation: string;
  }>;
}

export function AnalyticsPage() {
  const [showBands, setShowBands] = useState(false);
  const [markerFilter, setMarkerFilter] = useState<string>('');

  const engine = useQuery({
    queryKey: ['engine', showBands],
    queryFn: () => api.get<EngineData>(`/dashboard/engine?includeBands=${showBands}`),
  });

  const bands = engine.data?.scoreBands ?? [];
  const filteredBands = markerFilter
    ? bands.filter((band) => band.marker === markerFilter)
    : bands;

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="The scoring engine's reference tables."
      />

      <div className="space-y-6">
        {/* --- Engine reference ------------------------------------- */}
        {engine.data && (
          <>
            <Card title="Scoring engine">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-xs text-ink-muted">Version</p>
                  <p className="font-medium text-ink mt-0.5">{engine.data.version}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-muted">Overall formula</p>
                  <p className="font-mono text-[11px] text-ink mt-1 leading-relaxed">
                    {engine.data.overallFormula}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-ink-muted">Source checksum</p>
                  <p className="font-mono text-[10px] text-ink-muted mt-1 break-all">
                    {engine.data.sourceSha256.slice(0, 32)}…
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-ink/5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted mb-2.5">
                  Domain gates
                </p>
                <ul className="space-y-2 text-xs">
                  {engine.data.domainGates.map((gate) => (
                    <li key={gate.domain} className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-ink">{gate.domain}</span>
                      <span className="text-ink-muted">
                        ≥{gate.minMarkers}
                        {gate.ofMarkers ? ` of ${gate.ofMarkers}` : ''} markers, ≥
                        {gate.minWeightCoveragePct}% weight coverage
                      </span>
                      {gate.hardRequired.length > 0 && (
                        <span className="text-red-600">
                          mandatory: {gate.hardRequired.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            <Card title="Biomarkers" padded={false}>
              <Table>
                <thead>
                  <tr>
                    <Th>Marker</Th>
                    <Th>Domain</Th>
                    <Th>Type</Th>
                    <Th>Unit</Th>
                    <Th align="right">Weight</Th>
                    <Th align="center">Sex</Th>
                    <Th align="center">Required</Th>
                  </tr>
                </thead>
                <tbody>
                  {engine.data.biomarkers.map((marker) => (
                    <tr key={marker.markerId} className="hover:bg-ink/5/70">
                      <Td className="font-medium text-ink">{marker.name}</Td>
                      <Td className="text-ink-muted text-xs">{marker.domain}</Td>
                      <Td className="text-ink-muted text-xs">{marker.type}</Td>
                      <Td className="text-ink-muted text-xs font-mono">{marker.unit}</Td>
                      <Td align="right" className="text-ink">
                        {marker.defaultWeightPct > 0 ? `${marker.defaultWeightPct}%` : '-'}
                      </Td>
                      <Td align="center" className="text-xs text-ink-muted">
                        {marker.sexRule === 'Sex-specific' ? 'F / M' : 'All'}
                      </Td>
                      <Td align="center">
                        {marker.requiredForDomain && (
                          <Pill className="bg-blue-50 text-blue-700 ring-blue-200">yes</Pill>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>

            <Card
              title="Score bands"
              padded={false}
              action={
                <button
                  type="button"
                  onClick={() => setShowBands((open) => !open)}
                  className="text-xs font-medium text-[var(--brand-accent)] hover:underline"
                >
                  {showBands ? 'Hide' : 'Show all 150 bands'}
                </button>
              }
            >
              {!showBands ? (
                <div className="p-5 text-sm text-ink-muted">
                  The engine scores against 150 discrete bands. Values never interpolate between
                  them.
                </div>
              ) : (
                <>
                  <div className="p-4 border-b border-ink/5">
                    <select
                      className="rounded-sm2 border border-ink/10 px-3 py-1.5 text-sm"
                      value={markerFilter}
                      onChange={(event) => setMarkerFilter(event.target.value)}
                      aria-label="Filter bands by marker"
                    >
                      <option value="">All markers</option>
                      {[...new Set(bands.map((band) => band.marker))].sort().map((marker) => (
                        <option key={marker} value={marker}>{marker}</option>
                      ))}
                    </select>
                  </div>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Marker</Th>
                        <Th align="center">Sex</Th>
                        <Th align="right">Range</Th>
                        <Th align="right">Score</Th>
                        <Th>Label</Th>
                        <Th align="center">Priority</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBands.map((band, index) => (
                        <tr key={index} className="hover:bg-ink/5/70">
                          <Td className="text-xs font-mono text-ink-muted">{band.marker}</Td>
                          <Td align="center" className="text-xs text-ink-muted">{band.sex}</Td>
                          <Td align="right" className="text-xs font-mono text-ink">
                            {/* Inclusive/exclusive bounds shown explicitly, the
                                boundary rule is what makes bands unambiguous. */}
                            {band.lowerIncl ? '[' : '('}{band.lower}, {band.upper}
                            {band.upperIncl ? ']' : ')'}
                          </Td>
                          <Td align="right" className="font-medium text-ink">{band.score}</Td>
                          <Td className="text-xs text-ink-muted">{band.label}</Td>
                          <Td align="center">
                            <Pill
                              className={
                                band.priority === 'Escalate'
                                  ? 'bg-red-50 text-red-700 ring-red-200'
                                  : band.priority === 'Priority'
                                    ? 'bg-amber-50 text-amber-700 ring-amber-200'
                                    : band.priority === 'Review'
                                      ? 'bg-blue-50 text-blue-700 ring-blue-200'
                                      : 'bg-ink/5 text-ink-muted ring-ink/10'
                              }
                            >
                              {band.priority}
                            </Pill>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}
